import { type ComponentPropsWithoutRef, forwardRef, memo, useMemo } from 'react';

import { type Board, type PublicBoard } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import {
  BOARD_COVER_EDITOR_X_POSITION_CSS_VAR,
  BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR,
} from '~/components/v2/components/board/board-cover/BoardCoverEditor';
import { boardCoverPosition } from '~/components/v2/components/board/board-cover/boardCoverPosition';

export type BoardCoverProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'> & {
  board: Board | PublicBoard;
};

export const BoardCover = memo(
  forwardRef<HTMLImageElement, BoardCoverProps>(({ board, className }, ref) => {
    const region = board.coverImage?.region ?? { top: 0, left: 0, width: 100, height: 100 };

    const verticalPosition = useMemo(
      () => boardCoverPosition({ offset: region.top, extent: region.height }),
      [region.height, region.top],
    );

    const horizontalPosition = useMemo(
      () => boardCoverPosition({ offset: region.left, extent: region.width }),
      [region.left, region.width],
    );

    if (!board.coverImage) {
      return null;
    }

    return (
      <div className={cn('relative w-full shrink-0 overflow-hidden rounded-8 ads:bg-subtle bg-gray-6', className)}>
        {/* biome-ignore lint/performance/noImgElement: cover object-position requires a native img, which next/image cannot express */}
        <img
          ref={ref}
          src={board.coverImage.url}
          alt={`Cover for ${board.title ?? 'Untitled'} board`}
          className="absolute inset-0 size-full object-cover"
          data-testid="BOARD_COVER_IMAGE"
          style={{
            objectPosition: `var(${BOARD_COVER_EDITOR_X_POSITION_CSS_VAR}, ${horizontalPosition}%) var(${BOARD_COVER_EDITOR_Y_POSITION_CSS_VAR}, ${verticalPosition}%)`,
          }}
        />
      </div>
    );
  }),
);

BoardCover.displayName = 'BoardCover';
